import { Badge, Button, Col, Row } from "react-bootstrap";
import { useLoaderData, useNavigate, useRouteLoaderData } from "react-router-dom";
import { Invoice, ParkerDetails } from "./Data/ApiTypes";
import { cents, showMoney, Money, toCents } from "./Data/Money";
import { showDate, showInstantDate, showInstantDateTime } from "./Data/Date";
import { showAddressHtml } from "./Data/Location";
import { Envelope, Printer, Telephone } from "react-bootstrap-icons";
import { showOrderType } from "./Data/Finance";
import { LargePage } from "./Controls/LargePage";
import { equal, add } from "dinero.js";
import React from "react";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { toast } from "react-toastify";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { canPerformAction, extractClaims } from "./helpers/AuthorizationManager";
import { ToolButton } from "./Controls/PageBar";

export function FacilityParkerInvoiceDetailsPage() {
    const invoice  = useLoaderData()              as Invoice;
    const parker   = useRouteLoaderData("parker") as ParkerDetails;
    const nav = useNavigate();
    
    const [auth] = useAtom(authAtom);
    const userEmail = auth.account?.emailAddress; 
    const canUserReprintInvoice = canPerformAction(extractClaims(auth), 'can-reprint-invoice')

    const facility = invoice.facility;
    const orders   = invoice.orders;
    const addMoney = (a: Money, b: Money) => add(a, b);
    const totalOrderBalance = orders.reduce<Money>((sum, order) => addMoney(sum, order.balance.balance), cents(0));

    async function handleReprintClick() {
        const result = await Api.reprintInvoice(facility.facilityId, parker.parkerId, invoice.invoiceId);
        processResult(result, () => toast.success(`The invoice will be sent to you at ${userEmail} shortly.`), () => toast.error("Failed to reprint invoice. Please try again."));
    }

    return <LargePage>
        <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
                <div className="d-none d-md-block">
                    <Button variant="link" className="p-0" onClick={() => nav(-1)}>
                        ← Back
                    </Button>
                </div>
                {equal(totalOrderBalance, cents(0)) === true ? (
                    <Badge>Paid</Badge>
                ) : (
                    <Badge>Unpaid</Badge>
                )}
            </div>

  {canUserReprintInvoice && (
    <ToolButton onClick={handleReprintClick}>
      <Printer  color="maroon" />
      <span className="d-none d-sm-inline">Reprint</span>
    </ToolButton>
  )}
        </div>

        <div className="p-0 border-0 p-md-3 shadow-md mt-3 rounded-0">
            <Row>
                <Col xs={6}>
                    <div className="d-flex flex-column">
                        <strong className="fs-5 text-uppercase">
                            {facility.name}
                        </strong>
                        <div>
                            {showAddressHtml( facility.address )}
                        </div>
                        <div className="mt-1 d-flex-align-center gap-2">
                            <Envelope color="maroon" /> {facility.emailAddress}
                        </div>
                        <div className="d-flex-align-center gap-2">
                            <Telephone color="maroon" /> {facility.phoneNumber}
                        </div>
                    </div>
                </Col>
                <Col xs={6} className="text-md-end">
                    <div className="d-flex justify-content-end align-items-center gap-2">
                    <span>
                        <span className="fw-semibold text-secondary">INVOICE </span>
                        <span className="fw-bold">#{invoice.invoiceNumber}</span>
                    </span>

                    </div>
                    <div>
                        {showInstantDate( invoice.createdWhen, facility.timeZone )}
                    </div>
                </Col>
            </Row>
            <hr />
            <Row className="mb-4 gy-2">
                <Col lg={4}>
                    <div className="fs-4 text-secondary text-uppercase">
                        Parker
                    </div>
                    <div className="d-flex flex-column">
                        <div>
                            {parker.name}
                        </div>
                        <div className="d-flex-align-center gap-2">
                            <Envelope color="maroon" />{parker.emailAddress}
                        </div>
                        {!!parker.phoneNumber && <div className="d-flex-align-center gap-2">
                            <Telephone color="maroon" /> {parker.phoneNumber}
                        </div>}
                    </div>
                </Col>
                <Col lg={8}>
                    <div className="fs-4 ms-1 text-secondary text-uppercase">
                        Details
                    </div>
                    <table className="table">
                        <tbody>
                            <tr>
                                <td>Opening Balance</td>
                                <td>{showMoney( invoice.openingBalance )}</td>
                            </tr>
                            {orders.map((order, index) => (
                                <React.Fragment key={order.orderId}>
                                    <tr>
                                        <td colSpan={2} className="bg-light">
                                            <strong>Service {index + 1}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Service Type</td>
                                        <td>{showOrderType( order.orderType )}</td>
                                    </tr>
                                    <tr>
                                        <td>Service Details</td>
                                        <td>{order.summary}</td>
                                    </tr>
                                    <tr>
                                        <td>Service Amount</td>
                                        <td>{showMoney( order.price )}</td>
                                    </tr>
                                    {toCents(order.balance.totalPaid) > 0 && (
                                        <tr>
                                            <td>Payment</td>
                                            <td>({showMoney(order.balance.totalPaid)})</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Service Balance</td>
                                        <td>{showMoney( order.balance.balance )}</td>
                                    </tr>
                                </React.Fragment>
                            ))}
                            <tr className="table-secondary">
                                <td><strong>Total Service Balance</strong></td>
                                <td><strong>{showMoney( totalOrderBalance )}</strong></td>
                            </tr>
                            <tr>
                                <td>Ending Balance</td>
                                <td>{showMoney( invoice.endingBalance )}</td>
                            </tr>
                            <tr>
                                <td>Due Date</td>
                                <td>{showDate( invoice.dueWhen )}</td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="text-muted d-flex flex-column">
                        <div>Note: Make checks payable to {facility.name}</div>
                    </div>
                </Col>
            </Row>
        </div>
        <div className="d-flex flex-column justify-content-center gap-2 ms-2 mt-3 text-muted">
            {invoice.emailSentWhen !== null ? <div>
                Email Sent {showInstantDateTime( invoice.emailSentWhen, facility.timeZone )}
            </div> : <></>}
        </div>
        <Button type="button" className="w-50 mt-3 d-md-none" onClick={() => nav(-1)}>
            Back
        </Button>
    </LargePage>
}